import React from 'react';
import { last } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import Icon from 'shared/components/Icon';
import {
  PointScaleRatingType,
  QuestionTypeVariantType,
  ScalePropertiesRangeType,
  SurveyQuestionType,
  SurveyUiThemeType,
} from 'shared/types';
import { CustomRange } from './types';
import { SCORE_SIZE, TYPE_VARIANT_CHOICES_GLYPH } from './constants';
import TypeVariantIcon from './emoji';
import {
  LabelText,
  RangeNumber,
  StyledRange,
  TextHolder,
  TextLabel,
} from './style';

type RangeQuestionProps = {
  isFetchedTranslation?: boolean;
  value?: string;
  range: ScalePropertiesRangeType[];
  question: SurveyQuestionType;
  ui_theme: SurveyUiThemeType;
  transVariable?: any;
  brandColor?: string;
};

export const getRangeToRender = (
  question?: SurveyQuestionType,
): CustomRange[] => {
  const question_type = question?.type_info?.question_type;
  const question_theme = question?.type_info?.question_theme;
  const range = question?.type_info?.scale_properties?.range || [];
  if (!range.length) return [];
  const max = Number(range[range.length - 1]?.max);
  const min = Number(range[0]?.min);
  const order = question?.type_info?.scale_properties?.order;
  const labelValues =
    question?.type_info?.scale_properties?.labels?.values || [];
  const isNpsQuestion = question_type === 'RANGE' && question_theme === 'NPS';
  if (Number.isNaN(max) || Number.isNaN(min) || max <= 0) return [];
  const rangeToRender: CustomRange[] = Array.from({
    length: max + (1 - min),
  }).map((_, idx) => ({
    id: `${Math.round(Math.random() * 100)}_${idx}`,
    value: `${idx + min}`,
    text: labelValues[idx],
    position: idx + min,
  }));
  if (!rangeToRender.length) return [];
  if (isNpsQuestion) {
    rangeToRender[0].text = labelValues?.[0] || '';
    rangeToRender[rangeToRender.length - 1].text = labelValues?.[1] || '';
  }
  return order === 'ascending' ? rangeToRender : rangeToRender.reverse();
};

const RangeQuestion = ({
  question: q,
  value = '',
  isFetchedTranslation,
  range,
  ui_theme,
  transVariable,
  brandColor,
}: RangeQuestionProps) => {
  const question: SurveyQuestionType = {
    ...q,
    type_info: {
      ...q.type_info,
      scale_properties: {
        ...q.type_info?.scale_properties,
        labels: {
          ...q.type_info?.scale_properties?.labels,
          values:
            transVariable ||
            q.type_info?.scale_properties?.labels?.values ||
            [],
        },
      },
      footer_text: transVariable?.footer_text || q.type_info?.footer_text,
    },
  };
  const type_variant = question.type_info?.type_variant;
  const button_style =
    question?.type_info?.scale_properties?.button_style?.type;
  const question_type = question.type_info?.question_type;
  const question_theme = question.type_info?.question_theme;
  const preference = question?.type_info?.scale_properties?.labels?.preference;
  const max = last(range)?.max || 10;
  const isBooleanQuestionType =
    question_type === 'BOOLEAN' ||
    (range?.length === 2 && range?.[0].min === 0 && range?.[1].min === 1);
  const rangeToRender = React.useMemo(
    () => getRangeToRender(question || { name: '' }),
    [question],
  );
  if (type_variant === 'TEXT') {
    const rangeToDisplay = rangeToRender.find((r) => r.value === value);
    return (
      <>
        {!isFetchedTranslation ? (
          <>
            {value ? (
              <>
                <TextLabel
                  rangeItem={rangeToDisplay}
                  question={question}
                  ui_theme={ui_theme}
                  brandColor={brandColor}
                  showBrandColor
                >
                  {rangeToDisplay?.text}
                </TextLabel>
              </>
            ) : (
              '-'
            )}
          </>
        ) : (
          <Skeleton width={120} height={35} />
        )}
      </>
    );
  }

  if (
    (question_type === 'BOOLEAN' || isBooleanQuestionType) &&
    type_variant &&
    (['TICK', 'THUMBS', 'EMOJI'] as QuestionTypeVariantType[]).includes(
      type_variant,
    )
  ) {
    return (
      <TextHolder>
        {rangeToRender.map((r) => {
          const iconPath =
            TYPE_VARIANT_CHOICES_GLYPH?.[type_variant]?.[r?.position || 0];
          return (
            <TextLabel
              key={r.id}
              rangeItem={r}
              question={question}
              ui_theme={ui_theme}
              showBrandColor={r.value === value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                lineHeight: '17px',
                maxWidth: '280px',
                minWidth: '60px',
                justifyContent: 'center',
                wordBreak: 'break-word',
              }}
              brandColor={brandColor}
            >
              <Icon
                glyph={iconPath}
                fill="currentColor"
                viewBox="0 0 16 16"
                width={18}
                height={18}
                style={{ flex: '0 0 18px' }}
              />
              {preference !== 'none' && r?.text}
            </TextLabel>
          );
        })}
      </TextHolder>
    );
  }

  return (
    <StyledRange button_style={button_style}>
      <div className="m-s-b-s-q-r-widget">
        {rangeToRender.map((r) => (
          <div className="m-s-b-s-q-r-holder" key={r.id}>
            {type_variant === 'NUMBER' && (
              <RangeNumber
                rangeItem={r}
                question={question}
                ui_theme={ui_theme}
                showBrandColor={r.value === value}
                brandColor={brandColor}
              >
                {r.value}
              </RangeNumber>
            )}
            {type_variant &&
              (['EMOJI', 'STAR'] as QuestionTypeVariantType[]).includes(
                type_variant,
              ) && (
                <TypeVariantIcon
                  pointScale={max as PointScaleRatingType}
                  position={r.position - 1}
                  highlight={
                    type_variant === 'EMOJI'
                      ? value === r.value
                      : Number(value) >= r.position
                  }
                  button_style={button_style || 'standard'}
                  key={r.id}
                  type_variant={type_variant || 'EMOJI'}
                  size={SCORE_SIZE}
                />
              )}
            {preference !== 'none' && r.value === value && (
              <LabelText>
                <span className="r-q-text">{r.text}</span>
              </LabelText>
            )}
          </div>
        ))}
      </div>
    </StyledRange>
  );
};

export default RangeQuestion;
